import React, {useMemo, useRef, useState} from 'react';
import classNames from 'classnames';
import {useMountEffect} from "primereact";
import {Toast} from "primereact/toast";
import {Toolbar} from "primereact/toolbar";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {confirmDialog} from "primereact/confirmdialog";
import {isNullOrUndef} from "chart.js/helpers";
import {WorkloadCoefficientService} from "../service/WorkloadCoefficientService";
import {IncotermsService} from "../service/IncotermsService";
import {TradeTypesService} from "../service/TradeTypesService";

const EMPTY_FORM = {
    incotermId: null,
    tradeTypeId: null,
    statusId: null,
    coefficient: null
};

const buildCellKey = (incotermId, tradeTypeId, statusId) => `${incotermId}:${tradeTypeId}:${statusId}`;

const toNumberValue = (value) => {
    if (isNullOrUndef(value) || value === '') {
        return 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCoefficient = (value) => Number(toNumberValue(value).toFixed(4));

const decimalPlaces = (value) => {
    const valueString = String(value);
    if (!valueString.includes('.')) {
        return 0;
    }
    return valueString.split('.')[1].length;
};

export const WorkloadCoefficients = (props) => {

    const [pivotColumns, setPivotColumns] = useState([]);
    const [pivotRows, setPivotRows] = useState([]);
    const [records, setRecords] = useState([]);
    const [incoterms, setIncoterms] = useState([]);
    const [tradeTypes, setTradeTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingCells, setSavingCells] = useState({});
    const [editingCells, setEditingCells] = useState({});
    const [addDialogVisible, setAddDialogVisible] = useState(false);
    const [addForm, setAddForm] = useState(EMPTY_FORM);
    const [submitted, setSubmitted] = useState(false);

    const toast = useRef(null);
    const workloadCoefficientService = new WorkloadCoefficientService();
    const incotermsService = new IncotermsService();
    const tradeTypesService = new TradeTypesService();
    const canEdit = props.checkUserRole(['ROLE_ADMIN', 'EDIT_WORKLOAD_COEFFICIENTS']);

    const recordsByCellKey = useMemo(() => {
        const mapped = {};
        records.forEach(record => {
            mapped[buildCellKey(record.incotermId, record.tradeTypeId, record.statusId)] = record;
        });
        return mapped;
    }, [records]);

    const showError = (error, fallbackMessage = 'Unexpected error') => {
        const detail = !isNullOrUndef(error?.response?.data?.errorMessage)
            ? error.response.data.errorMessage
            : !isNullOrUndef(error?.message)
                ? error.message
                : fallbackMessage;
        toast.current.show({severity: 'error', summary: 'Error', detail, life: 5000});
    };

    const setCellSavingState = (cellKey, saving) => {
        setSavingCells(prevState => {
            const nextState = {...prevState};
            if (saving) {
                nextState[cellKey] = true;
            } else {
                delete nextState[cellKey];
            }
            return nextState;
        });
    };

    const setCellEditingState = (cellKey, editing) => {
        setEditingCells(prevState => {
            const nextState = {...prevState};
            if (editing) {
                nextState[cellKey] = true;
            } else {
                delete nextState[cellKey];
            }
            return nextState;
        });
    };

    const isCellEditing = (cellKey) => !!editingCells[cellKey];

    const hydratePivotRows = (rows, columns) => {
        return rows.map(row => {
            const statusCoefficients = {};
            columns.forEach(column => {
                const rawValue = !isNullOrUndef(row.statusCoefficients)
                    ? (row.statusCoefficients[column.statusId] ?? row.statusCoefficients[String(column.statusId)])
                    : 0;
                statusCoefficients[column.statusId] = normalizeCoefficient(rawValue);
            });

            return {
                ...row,
                statusCoefficients,
                total: normalizeCoefficient(row.total)
            };
        });
    };

    const loadReferences = () => {
        Promise.all([
            incotermsService.getIncoterms(),
            tradeTypesService.getTradeTypes()
        ]).then(([incotermsData, tradeTypesData]) => {
            setIncoterms(Array.isArray(incotermsData) ? incotermsData : []);
            setTradeTypes(Array.isArray(tradeTypesData) ? tradeTypesData : []);
        }).catch(error => showError(error, 'Failed to load reference data.'));
    };

    const loadWorkloadData = () => {
        setLoading(true);
        Promise.all([
            workloadCoefficientService.getWorkloadCoefficientsPivot(),
            workloadCoefficientService.getWorkloadCoefficients()
        ]).then(([pivotData, workloadRecords]) => {
            const columns = Array.isArray(pivotData?.columns) ? pivotData.columns : [];
            const rows = Array.isArray(pivotData?.rows) ? pivotData.rows : [];
            setPivotColumns(columns);
            setPivotRows(hydratePivotRows(rows, columns));
            setRecords(Array.isArray(workloadRecords) ? workloadRecords : []);
            setEditingCells({});
            setSavingCells({});
        }).catch(error => showError(error, 'Failed to load workload coefficients.'))
            .finally(() => setLoading(false));
    };

    useMountEffect(() => {
        loadReferences();
        loadWorkloadData();
    });

    const openAddDialog = () => {
        setSubmitted(false);
        setAddForm(EMPTY_FORM);
        setAddDialogVisible(true);
    };

    const hideAddDialog = () => {
        setSubmitted(false);
        setAddDialogVisible(false);
        setAddForm(EMPTY_FORM);
    };

    const onAddFieldChange = (value, field) => {
        const updated = {...addForm};
        updated[field] = value;
        setAddForm(updated);
    };

    const isValidAddForm = () => {
        if (isNullOrUndef(addForm.incotermId) || isNullOrUndef(addForm.tradeTypeId) || isNullOrUndef(addForm.statusId)) {
            return false;
        }
        if (isNullOrUndef(addForm.coefficient)) {
            return false;
        }

        const coefficient = Number(addForm.coefficient);
        return coefficient > 0 && coefficient <= 1 && decimalPlaces(coefficient) <= 4;
    };

    const saveNewCoefficient = () => {
        setSubmitted(true);
        if (!isValidAddForm()) {
            return;
        }

        const payload = {
            incotermId: addForm.incotermId,
            tradeTypeId: addForm.tradeTypeId,
            statusId: addForm.statusId,
            coefficient: normalizeCoefficient(addForm.coefficient)
        };

        workloadCoefficientService.saveWorkloadCoefficient(payload)
            .then(response => {
                if (!isNullOrUndef(response) && response.success) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Workload coefficient created',
                        life: 3000
                    });
                    hideAddDialog();
                    loadWorkloadData();
                } else {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: response?.errorMessage || 'Failed to save workload coefficient.',
                        life: 5000
                    });
                }
            })
            .catch(error => showError(error, 'Failed to save workload coefficient.'));
    };

    const getRowByKey = (incotermId, tradeTypeId) => {
        return pivotRows.find(row => row.incotermId === incotermId && row.tradeTypeId === tradeTypeId);
    };

    const getCellValue = (rowData, statusId) => {
        if (isNullOrUndef(rowData?.statusCoefficients)) {
            return 0;
        }
        const value = rowData.statusCoefficients[statusId] ?? rowData.statusCoefficients[String(statusId)];
        return normalizeCoefficient(value);
    };

    const updatePivotCellValue = (rowData, statusId, inputValue) => {
        const nextValue = isNullOrUndef(inputValue) ? 0 : normalizeCoefficient(inputValue);
        setPivotRows(prevRows => prevRows.map(row => {
            if (row.incotermId !== rowData.incotermId || row.tradeTypeId !== rowData.tradeTypeId) {
                return row;
            }

            const previousValue = getCellValue(row, statusId);
            const updatedTotal = normalizeCoefficient(toNumberValue(row.total) - previousValue + nextValue);

            return {
                ...row,
                statusCoefficients: {
                    ...row.statusCoefficients,
                    [statusId]: nextValue
                },
                total: updatedTotal
            };
        }));
    };

    const deleteCoefficientRecord = (record, showSuccessToast, onSuccess) => {
        const cellKey = buildCellKey(record.incotermId, record.tradeTypeId, record.statusId);
        setCellSavingState(cellKey, true);

        workloadCoefficientService.deleteWorkloadCoefficient(record.id)
            .then(response => {
                if (!isNullOrUndef(response) && response.success) {
                    if (showSuccessToast) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Workload coefficient deleted',
                            life: 3000
                        });
                    }
                    if (typeof onSuccess === 'function') {
                        onSuccess();
                    }
                    loadWorkloadData();
                } else {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: response?.errorMessage || 'Failed to delete workload coefficient.',
                        life: 5000
                    });
                    loadWorkloadData();
                }
            })
            .catch(error => {
                showError(error, 'Failed to delete workload coefficient.');
                loadWorkloadData();
            })
            .finally(() => setCellSavingState(cellKey, false));
    };

    const commitCellValue = (rowData, statusId, closeEditor = false) => {
        if (!canEdit) {
            return;
        }

        const row = getRowByKey(rowData.incotermId, rowData.tradeTypeId);
        if (isNullOrUndef(row)) {
            return;
        }

        const cellKey = buildCellKey(row.incotermId, row.tradeTypeId, statusId);
        if (savingCells[cellKey]) {
            return;
        }

        const existingRecord = recordsByCellKey[cellKey];
        const currentValue = normalizeCoefficient(getCellValue(row, statusId));
        const existingValue = !isNullOrUndef(existingRecord) ? normalizeCoefficient(existingRecord.coefficient) : 0;

        if (currentValue === existingValue) {
            if (closeEditor) {
                setCellEditingState(cellKey, false);
            }
            return;
        }

        if (currentValue < 0 || currentValue > 1 || decimalPlaces(currentValue) > 4) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Coefficient must be between 0.0000 and 1.0000.',
                life: 5000
            });
            return;
        }

        if (currentValue === 0) {
            if (!isNullOrUndef(existingRecord)) {
                deleteCoefficientRecord(existingRecord, false, () => {
                    if (closeEditor) {
                        setCellEditingState(cellKey, false);
                    }
                });
            } else if (closeEditor) {
                setCellEditingState(cellKey, false);
            }
            return;
        }

        const payload = {
            id: existingRecord?.id ?? null,
            incotermId: row.incotermId,
            tradeTypeId: row.tradeTypeId,
            statusId,
            coefficient: currentValue
        };

        setCellSavingState(cellKey, true);
        workloadCoefficientService.saveWorkloadCoefficient(payload)
            .then(response => {
                if (!isNullOrUndef(response) && response.success) {
                    if (closeEditor) {
                        setCellEditingState(cellKey, false);
                    }
                    loadWorkloadData();
                } else {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: response?.errorMessage || 'Failed to save workload coefficient.',
                        life: 5000
                    });
                    loadWorkloadData();
                }
            })
            .catch(error => {
                showError(error, 'Failed to save workload coefficient.');
                loadWorkloadData();
            })
            .finally(() => setCellSavingState(cellKey, false));
    };

    const onEditCellToggle = (rowData, statusId) => {
        const cellKey = buildCellKey(rowData.incotermId, rowData.tradeTypeId, statusId);
        if (savingCells[cellKey]) {
            return;
        }

        if (isCellEditing(cellKey)) {
            commitCellValue(rowData, statusId, true);
        } else {
            setCellEditingState(cellKey, true);
        }
    };

    const confirmDeleteCell = (rowData, statusId) => {
        const cellKey = buildCellKey(rowData.incotermId, rowData.tradeTypeId, statusId);
        const record = recordsByCellKey[cellKey];
        if (isNullOrUndef(record)) {
            return;
        }

        confirmDialog({
            message: `Delete coefficient for ${rowData.incotermTitle} / ${rowData.tradeTypeName}?`,
            icon: 'pi pi-exclamation-triangle',
            accept: () => deleteCoefficientRecord(record, true, null),
            reject: () => null
        });
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="table-header">
                <h5 className="p-m-0">Workload coefficients</h5>
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        if (!canEdit) {
            return null;
        }

        return (
            <Button label="Add" icon="pi pi-plus" className="p-button-success p-mr-2 p-mb-2"
                    onClick={openAddDialog}/>
        );
    };

    const coefficientCellTemplate = (statusId) => {
        return (rowData) => {
            const cellKey = buildCellKey(rowData.incotermId, rowData.tradeTypeId, statusId);
            const record = recordsByCellKey[cellKey];
            const editing = isCellEditing(cellKey);
            const currentValue = getCellValue(rowData, statusId);
            const displayValue = !isNullOrUndef(record) || currentValue !== 0 ? currentValue : null;

            if (!canEdit) {
                return <span>{!isNullOrUndef(record) ? currentValue : ''}</span>;
            }

            return (
                <div className="workload-coef-actions">
                    <InputNumber value={displayValue}
                                 className="workload-coef-input"
                                 inputStyle={{width: '3.6rem'}}
                                 useGrouping={false}
                                 min={0}
                                 max={1}
                                 step={0.1}
                                 showButtons
                                 buttonLayout="horizontal"
                                 minFractionDigits={0}
                                 maxFractionDigits={4}
                                 disabled={!!savingCells[cellKey] || !editing}
                                 onValueChange={(e) => updatePivotCellValue(rowData, statusId, e.value)}/>
                    <Button icon={editing ? 'pi pi-check' : 'pi pi-pencil'}
                            className={classNames('workload-coef-action-btn p-button-text p-button-sm',
                                {'p-button-info': editing, 'p-button-success': !editing})}
                            title={editing ? 'Save' : 'Edit'}
                            disabled={!!savingCells[cellKey]}
                            onClick={() => onEditCellToggle(rowData, statusId)}/>
                    {!isNullOrUndef(record) ?
                        <Button icon="pi pi-trash" className="workload-coef-action-btn p-button-danger p-button-text p-button-sm"
                                title={'Delete'} disabled={!!savingCells[cellKey]}
                                onClick={() => confirmDeleteCell(rowData, statusId)}/> : null}
                </div>
            );
        };
    };

    const addDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideAddDialog}/>
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={saveNewCoefficient}/>
        </>
    );

    return (
        <div className="p-grid crud-demo">
            <div className="p-col-12">
                <div className="card">
                    <Toast ref={toast}/>
                    <Toolbar className="p-mb-4 p-toolbar" left={leftToolbarTemplate} right={rightToolbarTemplate}/>

                    <DataTable value={pivotRows}
                               loading={loading}
                               className="datatable-responsive workload-coef-table"
                               responsiveLayout="scroll"
                               scrollable
                               scrollHeight="flex"
                               emptyMessage="No workload coefficients found.">
                        <Column field="incotermTitle" header="Incoterm" style={{minWidth: '8.5rem'}}/>
                        <Column field="tradeTypeName" header="Trade type" style={{minWidth: '8.5rem'}}/>
                        {pivotColumns.map(column => (
                            <Column key={column.statusId}
                                    header={column.statusName}
                                    body={coefficientCellTemplate(column.statusId)}
                                    style={{minWidth: '8.25rem'}}/>
                        ))}
                        <Column field="total" header="Total" style={{minWidth: '5.5rem'}}
                                body={rowData => normalizeCoefficient(rowData.total)}/>
                    </DataTable>

                    <Dialog visible={addDialogVisible}
                            style={{width: '480px'}}
                            header="Add workload coefficient"
                            modal
                            className="p-fluid"
                            footer={addDialogFooter}
                            onHide={hideAddDialog}>
                        <div className="p-field">
                            <label htmlFor="workload-incoterm">Incoterm</label>
                            <Dropdown id="workload-incoterm"
                                      value={addForm.incotermId}
                                      options={incoterms}
                                      optionLabel="title"
                                      optionValue="id"
                                      filter
                                      showClear
                                      onChange={(e) => onAddFieldChange(e.value, 'incotermId')}
                                      className={classNames({'p-invalid': submitted && isNullOrUndef(addForm.incotermId)})}/>
                            {submitted && isNullOrUndef(addForm.incotermId) ?
                                <small className="p-invalid">Incoterm is required.</small> : null}
                        </div>

                        <div className="p-field">
                            <label htmlFor="workload-trade-type">Trade type</label>
                            <Dropdown id="workload-trade-type"
                                      value={addForm.tradeTypeId}
                                      options={tradeTypes}
                                      optionLabel="name"
                                      optionValue="id"
                                      filter
                                      showClear
                                      onChange={(e) => onAddFieldChange(e.value, 'tradeTypeId')}
                                      className={classNames({'p-invalid': submitted && isNullOrUndef(addForm.tradeTypeId)})}/>
                            {submitted && isNullOrUndef(addForm.tradeTypeId) ?
                                <small className="p-invalid">Trade type is required.</small> : null}
                        </div>

                        <div className="p-field">
                            <label htmlFor="workload-status">Status</label>
                            <Dropdown id="workload-status"
                                      value={addForm.statusId}
                                      options={pivotColumns}
                                      optionLabel="statusName"
                                      optionValue="statusId"
                                      filter
                                      showClear
                                      onChange={(e) => onAddFieldChange(e.value, 'statusId')}
                                      className={classNames({'p-invalid': submitted && isNullOrUndef(addForm.statusId)})}/>
                            {submitted && isNullOrUndef(addForm.statusId) ?
                                <small className="p-invalid">Status is required.</small> : null}
                        </div>

                        <div className="p-field">
                            <label htmlFor="workload-coefficient">Coefficient</label>
                            <InputNumber id="workload-coefficient"
                                         value={addForm.coefficient}
                                         useGrouping={false}
                                         min={0}
                                         max={1}
                                         step={0.1}
                                         showButtons
                                         buttonLayout="horizontal"
                                         minFractionDigits={0}
                                         maxFractionDigits={4}
                                         onValueChange={(e) => onAddFieldChange(e.value, 'coefficient')}
                                         className={classNames('workload-coef-input', {'p-invalid': submitted && !isValidAddForm()})}/>
                            {submitted && isNullOrUndef(addForm.coefficient) ?
                                <small className="p-invalid">Coefficient is required.</small> : null}
                            {submitted && !isNullOrUndef(addForm.coefficient)
                                && (Number(addForm.coefficient) <= 0 || Number(addForm.coefficient) > 1) ?
                                <small className="p-invalid">Coefficient must be greater than 0 and less than or equal to 1.</small> : null}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
}
